import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { NumberInput } from '@/components/ui/number-input';
import { DatePicker } from '@/components/ui/date-picker';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AccountSelect } from '@/components/accounting/ui/account-select';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { CurrencySelector } from '@/components/ui/currency-selector';
import { AccountCarbonSettings } from '@/components/carbon/AccountCarbonSettings';
import { AccountBankFeedSection } from '@/components/banking/AccountBankFeedSection';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationTaxRates } from '@/hooks/useOrganizationTaxSettings';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';
import { isCreditCardType, isFeedAccountType } from '@/lib/bankAccount';
import {
  useChartOfAccounts,
  useCreateAccount,
  useUpdateAccount,
  useAccountCategories,
  useAccountTypes,
  useAccountSubtypes,
  useAccountOpeningBalance,
  buildAccountHierarchy,
} from '@/hooks/useChartOfAccounts';
import { parseLocalISODate, formatLocalISODate } from '@/lib/dateUtils';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';
import { useShowAccountNumbers } from '@/hooks/useShowAccountNumbers';
import {
  CONTRA_SUB_ACCOUNTS_BLOCK,
  NORMAL_BALANCE_CHOICE_REQUIRED,
  childRuleSide,
  contraSubAccountBlocks,
  formatAccountLabel,
  isChildSubtypeAllowed,
  subtypeRuleSide,
  type NormalBalanceChoiceConflict,
} from '@releve/shared';
import { useNormalBalanceChoice } from '@/components/accounting/ui/normal-balance-choice';
import {
  ACCOUNT_CLASSIFICATION_ERROR_FIELD,
  type AccountClassificationErrorCode,
  type ChartOfAccount,
  type CreateAccountData,
  type HierarchicalAccount,
  type UpdateAccountData,
} from '@/types/financial-accounts';

/** The OBE type is a system mechanism (opening balances) — never a pick. */
const OBE_TYPE_NAME = 'Opening Balance Equity';
/** UI nesting cap; the update_account_classification RPC enforces the same. */
const MAX_ACCOUNT_DEPTH = 3;
const CLASSIFICATION_FIELDS = ['creation_type', 'type_id', 'subtype_id', 'parent_account_id'] as const;

interface CreateOrUpdateAccountModalProps {
  organizationId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: ChartOfAccount | null; // If provided, we're editing
  /**
   * When true (default), a successful create navigates to the new account's detail page
   * (mirrors CreateProjectDialog). Callers that should stay in place after create
   * (e.g. Bank Accounts, Accounting Overview) pass `false`. Has no effect on edit.
   */
  navigateOnCreate?: boolean;
  /**
   * Preselect the account classification on create (e.g. the Credit Cards page presets
   * Liabilities → Credit Cards). `normal_balance` is derived from the preset category.
   * Ignored when editing. The caller resolves these ids from the same reference-data cache
   * the modal uses, so `categories` is already populated when a non-empty id is passed.
   */
  defaultCategoryId?: string;
  defaultTypeId?: string;
  /**
   * Hands the bank connect/reconnect flow to the host, which CLOSES this modal and
   * opens the connect dialog with an explicit `returnTo`. The host owns it because
   * that flow ends in a full-page redirect: leaving the modal open through the
   * navigation strands its `{fullscreenModal:true}` history entry as a dead Back
   * press after the callback.
   *
   * It also GATES the Bank feed section — a host that cannot take the redirect
   * shows no feed settings at all (AccountDetails deliberately does not wire it).
   */
  onRequestBankConnect?: (connectionId: string | null) => void;
}

interface AccountFormData {
  account_name: string;
  account_description?: string;
  /** UI-only: drives the main/sub toggle and create-mode validation. Not persisted. */
  creation_type: 'main' | 'sub';
  category_id: string;
  type_id: string;
  subtype_id?: string;
  parent_account_id?: string;
  normal_balance: 'debit' | 'credit';
  can_have_children: boolean;
  requires_project: boolean;
  opening_balance: number;
  opening_balance_date?: string;
  default_tax_rate_id?: string;
  currency?: string;
}

interface CarbonConfigState {
  enabled: boolean;
  tableId: string;
  multiplierCode: string;
  multiplierLabel: string;
  multiplierUnit: string;
  dimensionFilter: Record<string, string>;
  multiplierUnitCode: string | null;
}

const EMPTY_ACCOUNT_FORM: AccountFormData = {
  account_name: '',
  account_description: '',
  creation_type: 'main',
  category_id: '',
  type_id: '',
  subtype_id: '',
  parent_account_id: '',
  normal_balance: 'debit',
  can_have_children: false,
  requires_project: false,
  opening_balance: 0,
  opening_balance_date: '',
  default_tax_rate_id: '',
  currency: '',
};

const EMPTY_CARBON: CarbonConfigState = {
  enabled: false,
  tableId: '',
  multiplierCode: '',
  multiplierLabel: '',
  multiplierUnit: '',
  dimensionFilter: {},
  multiplierUnitCode: null,
};

const SECTION_HEAD = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3';
const FIELD_LABEL = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// Flatten the account hierarchy (parents before children, alphabetical) for the parent dropdown.
function flattenHierarchyForForms(hierarchicalAccounts: HierarchicalAccount[]): ChartOfAccount[] {
  const flattened: ChartOfAccount[] = [];
  const sorted = [...hierarchicalAccounts].sort((a, b) => a.account_name.localeCompare(b.account_name));
  const addAccountAndChildren = (acct: HierarchicalAccount) => {
    const { children, ...accountWithoutChildren } = acct;
    flattened.push(accountWithoutChildren as ChartOfAccount);
    if (children && children.length > 0) {
      [...children]
        .sort((a, b) => a.account_name.localeCompare(b.account_name))
        .forEach((child) => addAccountAndChildren(child));
    }
  };
  sorted.forEach((acct) => addAccountAndChildren(acct));
  return flattened;
}

interface Subtree {
  /** Every descendant id (not the root). */
  ids: Set<string>;
  /** Levels below the root: 0 = leaf, 1 = has children, 2 = has grandchildren. */
  depth: number;
}
const EMPTY_SUBTREE: Subtree = { ids: new Set(), depth: 0 };

/**
 * Walk the flat account list down from `rootId`. Feeds the edit-mode parent
 * picker: an account cannot be nested under itself or a descendant, and the
 * moved subtree must fit under the depth cap.
 */
function subtreeOf(accounts: ChartOfAccount[], root: ChartOfAccount): Subtree {
  const rootId = root.id;
  const byParent = new Map<string, ChartOfAccount[]>();
  for (const acc of accounts) {
    if (!acc.parent_account_id) continue;
    const list = byParent.get(acc.parent_account_id) ?? [];
    list.push(acc);
    byParent.set(acc.parent_account_id, list);
  }
  const ids = new Set<string>();
  let depth = 0;
  let frontier = byParent.get(rootId) ?? [];
  while (frontier.length > 0 && depth < 10) {
    depth += 1;
    const next: ChartOfAccount[] = [];
    for (const acc of frontier) {
      if (ids.has(acc.id)) continue;
      ids.add(acc.id);
      next.push(...(byParent.get(acc.id) ?? []));
    }
    frontier = next;
  }
  return { ids, depth };
}

export function CreateOrUpdateAccountModal({
  organizationId,
  open,
  onOpenChange,
  account,
  navigateOnCreate = true,
  defaultCategoryId,
  defaultTypeId,
  onRequestBankConnect,
}: CreateOrUpdateAccountModalProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const orgNavigate = useOrgNavigate();
  const isEditing = !!account;

  // Carbon tracking config (lives outside RHF; AccountCarbonSettings is prop-driven).
  const [carbon, setCarbon] = useState<CarbonConfigState>(EMPTY_CARBON);
  const [carbonDirty, setCarbonDirty] = useState(false);

  // Stable per-field updaters that also mark carbon dirty (so close confirmation fires).
  const carbonHandlers = useMemo(
    () => ({
      onEnabledChange: (v: boolean) => { setCarbon((p) => ({ ...p, enabled: v })); setCarbonDirty(true); },
      onTableIdChange: (v: string) => { setCarbon((p) => ({ ...p, tableId: v })); setCarbonDirty(true); },
      onMultiplierCodeChange: (v: string) => { setCarbon((p) => ({ ...p, multiplierCode: v })); setCarbonDirty(true); },
      onMultiplierLabelChange: (v: string) => { setCarbon((p) => ({ ...p, multiplierLabel: v })); setCarbonDirty(true); },
      onMultiplierUnitChange: (v: string) => { setCarbon((p) => ({ ...p, multiplierUnit: v })); setCarbonDirty(true); },
      onDimensionFilterChange: (v: Record<string, string>) => { setCarbon((p) => ({ ...p, dimensionFilter: v })); setCarbonDirty(true); },
      onMultiplierUnitCodeChange: (v: string | null) => { setCarbon((p) => ({ ...p, multiplierUnitCode: v })); setCarbonDirty(true); },
    }),
    [],
  );

  // Data hooks
  const { data: accounts } = useChartOfAccounts(organizationId);
  const { data: categories } = useAccountCategories();
  const { data: types } = useAccountTypes();
  const { data: taxRates } = useOrganizationTaxRates(organizationId);
  const createAccountMutation = useCreateAccount();
  const updateAccountMutation = useUpdateAccount();
  // A reclassification that would replace a manual normal-balance override
  // comes back as a 409; the user picks keep / standard and the save resends.
  const showAccountNumbers = useShowAccountNumbers();
  const normalBalanceChoice = useNormalBalanceChoice({ showAccountNumbers });

  // UNCONDITIONAL, per the hooks rule — the SECTION is what mounts conditionally.
  // That distinction is load-bearing: this modal is mounted unconditionally by
  // AccountDetails (gated only by `open`), so no query hook may live up here, and
  // `useBankConnections` therefore stays inside AccountBankFeedSection.
  const { enabled: syncEnabled } = useFeatureEnabled('bank_feed_sync');
  // The type join is present on most read paths; the types cache covers the rest.
  const resolvedTypeName =
    account?.type?.type_name ??
    types?.find((accountType) => accountType.id === account?.type_id)?.type_name ??
    null;

  // Current opening balance = the account's live opening journals (the retired
  // opening_balance column is always 0 — see set_account_opening_balance).
  // Gated on open: AccountDetails mounts this modal unconditionally, and the
  // conditional-mounting rule forbids firing queries for a closed dialog.
  const openingQuery = useAccountOpeningBalance(organizationId, account?.id, {
    enabled: open && isEditing,
  });
  // The account cannot carry its own opening balance: OBE accounts are the
  // offset side of the mechanism (INVALID_ACCOUNT server-side).
  const isObeAccount =
    account?.system_account_type === 'opening_balance_equity' ||
    account?.type?.type_name === 'Opening Balance Equity';
  // A live opening journal touching a third account is not the field's to
  // manage (OPENING_SHARED server-side) — adjust via a manual journal entry.
  const openingLocked = isEditing && (isObeAccount || openingQuery.data?.shared === true);
  // Until the read resolves, submitting could send a wrong 0 — the fields stay
  // disabled and (belt and braces) the submit payload omits pristine fields.
  const openingLoading = isEditing && openingQuery.isPending;
  // Classification (type / subtype / parent) is editable within the locked
  // category — except on system accounts and the OBE family, which the RPC
  // refuses (SYSTEM_ACCOUNT / OBE_ACCOUNT); show them read-only instead.
  const classificationLocked = isEditing && (!!account?.system_account_type || isObeAccount);

  // Fetch carbon config for editing
  const { data: existingCarbonConfig } = useQuery({
    queryKey: ['carbonConfig', account?.id],
    queryFn: async () => {
      if (!account?.id) return null;
      const { data, error } = await supabase
        .from('carbon_account_emission_tables')
        .select('*')
        .eq('account_id', account.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: isEditing && !!account?.id,
  });

  // The schema's superRefine needs per-field dirtiness, but the form is
  // created FROM the schema — break the cycle with a ref (stable identity, so
  // it doesn't belong in the memo deps).
  const formRef = useRef<UseFormReturn<AccountFormData> | null>(null);

  // Validation schema — classification is only required on create (locked in edit).
  const schema = useMemo(
    () =>
      z
        .object({
          account_name: z.string().min(1, t('chartOfAccountsForm.validation.accountNameRequired')),
          account_description: z.string().optional(),
          creation_type: z.enum(['main', 'sub']),
          category_id: z.string(),
          type_id: z.string(),
          subtype_id: z.string().optional(),
          parent_account_id: z.string().optional(),
          normal_balance: z.enum(['debit', 'credit']),
          can_have_children: z.boolean(),
          requires_project: z.boolean(),
          opening_balance: z.number(),
          opening_balance_date: z.string().optional(),
          default_tax_rate_id: z.string().optional(),
          currency: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          // Opening balance posts a dated journal — a non-zero amount needs
          // its journal date (the API enforces it too). Gated on the fields
          // being DIRTY: a backfilled account can legitimately hold multiple
          // live opening journals with differing dates, which seeds a nonzero
          // amount with an EMPTY date — an untouched save must not trip
          // validation on fields the user never edited (the PATCH omits
          // pristine opening fields entirely).
          const openingDirty =
            formRef.current?.getFieldState('opening_balance').isDirty ||
            formRef.current?.getFieldState('opening_balance_date').isDirty;
          if (openingDirty && (data.opening_balance ?? 0) !== 0 && !data.opening_balance_date) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['opening_balance_date'],
              message: t('chartOfAccountsForm.validation.openingBalanceDateRequired'),
            });
          }
          if (isEditing) {
            // Category is locked; the classification section only edits type /
            // subtype / parent. A sub-account needs its parent (the type is
            // inherited from it); a top-level account needs a type.
            if (data.creation_type === 'sub' && !data.parent_account_id) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['parent_account_id'],
                message: t('chartOfAccountsForm.validation.parentRequired'),
              });
            } else if (!data.type_id) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['type_id'],
                message: t('chartOfAccountsForm.validation.typeRequired'),
              });
            }
            return;
          }
          // A main account needs an explicit category + type; a sub-account needs a parent
          // (its category/type/normal_balance are inherited from the parent, which is NOT NULL
          // on chart_of_accounts). Validate the parent — not the inherited fields — so every
          // error lands on a field the user can actually see and clear.
          if (data.creation_type === 'main') {
            if (!data.category_id) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['category_id'],
                message: t('chartOfAccountsForm.validation.categoryRequired'),
              });
            }
            if (!data.type_id) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['type_id'],
                message: t('chartOfAccountsForm.validation.typeRequired'),
              });
            }
          } else if (!data.parent_account_id) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['parent_account_id'],
              message: t('chartOfAccountsForm.validation.parentRequired'),
            });
          }
        }),
    [t, isEditing],
  );

  const form = useForm<AccountFormData>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_ACCOUNT_FORM,
  });
  formRef.current = form;

  // Watched values driving dependent fields
  const creationType = form.watch('creation_type');
  const selectedCategoryId = form.watch('category_id');
  const selectedTypeId = form.watch('type_id');

  // Get filtered types based on selected category
  const availableTypes = types?.filter((type) => type.category_id === selectedCategoryId) || [];

  // The subtypes this org may use (universal + its country's), every type at
  // once; the picker filters by the selected type.
  const { data: orgSubtypes } = useAccountSubtypes(organizationId);
  const selectedParentId = form.watch('parent_account_id');
  const selectedSubtypeId = form.watch('subtype_id');
  /** The type's general ("Other …") subtype — what the server fills when none is sent. */
  const generalSubtypeIdOf = (typeId: string | undefined) =>
    typeId ? orgSubtypes?.find((st) => st.type_id === typeId && st.is_general)?.id : undefined;

  // Edit mode: the account's own subtree drives which parents are offered.
  const subtree = useMemo(
    () => (isEditing && account && accounts ? subtreeOf(accounts, account) : EMPTY_SUBTREE),
    [isEditing, account, accounts],
  );
  const childCount = isEditing && account && accounts
    ? accounts.filter((a) => a.parent_account_id === account.id).length
    : 0;
  // A sub-account's type follows its parent (the server copies it on insert
  // and on re-parent) — the type select is locked. Its subtype is the
  // parent's or a CONTRA subtype of the parent's type (a contra account nested
  // under the account it reduces: Owner draws under Owner capital, Accumulated
  // depreciation under the asset) — the subtype picker offers just those.
  const typeInherited = creationType === 'sub' && !!selectedParentId;
  const selectedTypeName = types?.find((accountType) => accountType.id === selectedTypeId)?.type_name ?? null;

  // Determine recommended tax direction based on account category
  const selectedCategoryName = categories?.find((c) => c.id === selectedCategoryId)?.category_name;
  const recommendedTaxDirection = selectedCategoryName === 'Revenue' ? 'OUTPUT' : 'INPUT';

  // Group active tax rates: recommended direction first, then other direction
  const { recommendedRates, otherRates } = useMemo(() => {
    const activeRates = taxRates?.filter((r) => r.is_active) ?? [];
    return {
      recommendedRates: activeRates.filter((r) => r.direction === recommendedTaxDirection),
      otherRates: activeRates.filter((r) => r.direction !== recommendedTaxDirection),
    };
  }, [taxRates, recommendedTaxDirection]);

  // Reset the RHF form when the dialog opens or a DIFFERENT account is edited. Keyed on
  // account?.id (not the object, and NOT existingCarbonConfig): a background refetch that
  // returns the same row hands back a new object reference, and re-running reset() on that
  // would wipe edits the user already typed. Carbon loads in its own effect below.
  useEffect(() => {
    if (!open) return;
    if (isEditing && account) {
      form.reset({
        account_name: account.account_name,
        account_description: account.account_description || '',
        creation_type: account.parent_account_id ? 'sub' : 'main',
        category_id: account.category_id,
        type_id: account.type_id,
        subtype_id: account.subtype_id || '',
        parent_account_id: account.parent_account_id || '',
        normal_balance: account.normal_balance,
        can_have_children: account.can_have_children,
        requires_project: account.requires_project,
        // Warm cache: seed straight from the read INSIDE the reset — reset()
        // empties RHF's field registry (`_fields = {}`), and resetField is a
        // silent no-op for unregistered names, so a follow-up effect in the
        // same flush cannot seed an already-cached value. Cold cache: these
        // read undefined here and the seeding effect below fills them once
        // the query resolves (fields are re-registered by then). The retired
        // account-row columns are always 0/NULL and never used.
        opening_balance: openingQuery.data?.amount ?? 0,
        opening_balance_date: openingQuery.data?.effectiveDate ?? '',
        default_tax_rate_id: account.default_tax_rate_id ?? '',
        currency: account.currency || '',
      });
    } else {
      // Create mode — optionally preselect the classification (e.g. Credit Cards page presets
      // Liabilities → Credit Cards). normal_balance follows the preset category so the account
      // is created credit-normal, which the running-balance/posting logic depends on.
      const presetCategory = defaultCategoryId
        ? categories?.find((c) => c.id === defaultCategoryId)
        : undefined;
      form.reset({
        ...EMPTY_ACCOUNT_FORM,
        category_id: defaultCategoryId || '',
        type_id: defaultTypeId || '',
        normal_balance: presetCategory?.normal_balance ?? EMPTY_ACCOUNT_FORM.normal_balance,
      });
    }
    setCarbonDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on account?.id on purpose; reading the latest account values inside is intended
  }, [open, isEditing, account?.id, defaultCategoryId, defaultTypeId, form]);

  // Correct the preset account's normal_balance once category reference data loads. The reset
  // effect above derives it from `categories`, but on a cold cache that can be undefined at open
  // time (falling back to 'debit'). This deterministically sets it from the preset category —
  // guarded on the field being pristine so it never overwrites a manual override.
  useEffect(() => {
    if (!open || isEditing || !defaultCategoryId) return;
    const presetCategory = categories?.find((c) => c.id === defaultCategoryId);
    if (
      presetCategory &&
      form.getValues('category_id') === defaultCategoryId &&
      !form.getFieldState('normal_balance').isDirty
    ) {
      form.setValue('normal_balance', presetCategory.normal_balance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the preset or categories change
  }, [open, isEditing, defaultCategoryId, categories]);

  // A type with no subtype picked shows its general subtype — a preview of
  // what the server fills (trg_assign_account_number). Covers a preset type
  // and a type picked before the org's subtypes loaded; not dirtying, so an
  // untouched form stays pristine.
  useEffect(() => {
    if (!open || typeInherited || !selectedTypeId || selectedSubtypeId) return;
    const generalId = generalSubtypeIdOf(selectedTypeId);
    if (generalId) form.setValue('subtype_id', generalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- generalSubtypeIdOf reads orgSubtypes, listed
  }, [open, typeInherited, selectedTypeId, selectedSubtypeId, orgSubtypes, form]);

  // Seed the opening-balance fields once their read resolves (carbon-effect
  // pattern: async-loaded state, dirty-guarded). resetField (not setValue)
  // makes the loaded value the field's DEFAULT, so dirtiness — which gates
  // whether the submit payload includes the fields at all — means "the user
  // actually changed it", and a quick rename submitted before this resolves
  // cannot silently zero the opening journal.
  useEffect(() => {
    if (!open || !isEditing || !openingQuery.data) return;
    if (
      form.getFieldState('opening_balance').isDirty ||
      form.getFieldState('opening_balance_date').isDirty
    ) {
      return;
    }
    form.resetField('opening_balance', { defaultValue: openingQuery.data.amount });
    form.resetField('opening_balance_date', {
      defaultValue: openingQuery.data.effectiveDate ?? '',
    });
  }, [open, isEditing, openingQuery.data, form]);

  // Load carbon config once the query resolves. Guarded on !carbonDirty so a background
  // refetch of the carbon (or account) query can't overwrite carbon edits the user has made
  // while the dialog is open. carbonDirty is reset per-open by the form-reset effect above.
  useEffect(() => {
    if (!open || carbonDirty) return;
    if (isEditing && existingCarbonConfig) {
      setCarbon({
        enabled: existingCarbonConfig.is_enabled ?? false,
        tableId: existingCarbonConfig.table_id || '',
        multiplierCode: existingCarbonConfig.multiplier_dimension_code || '',
        multiplierLabel: existingCarbonConfig.multiplier_dimension_label || '',
        multiplierUnit: existingCarbonConfig.multiplier_unit || '',
        dimensionFilter: (existingCarbonConfig.dimension_filter as Record<string, string> | null) ?? {},
        multiplierUnitCode: (existingCarbonConfig.multiplier_unit_code as string | null) ?? null,
      });
    } else {
      setCarbon(EMPTY_CARBON);
    }
  }, [open, isEditing, existingCarbonConfig, carbonDirty]);

  const filteredAccounts = useMemo(
    () => (accounts ? flattenHierarchyForForms(buildAccountHierarchy(accounts)) : []),
    [accounts],
  );

  // Dependent-field handlers
  const handleCreationTypeChange = (value: 'main' | 'sub') => {
    form.setValue('creation_type', value, { shouldDirty: true });
    form.setValue('parent_account_id', '', { shouldDirty: true });
    if (isEditing) {
      // Category (and normal balance) are locked on edit; the type stays as-is
      // so a sub-account promoted to top level keeps its inherited type until
      // the user picks another.
      return;
    }
    form.setValue('category_id', '', { shouldDirty: true });
    form.setValue('type_id', '', { shouldDirty: true });
    form.setValue('subtype_id', '', { shouldDirty: true });
    // Cleared so a stale value from the prior path can't leak through; it's repopulated by
    // the category (main) or parent (sub) selection before a valid submit.
    form.setValue('normal_balance', 'debit', { shouldDirty: true });
  };

  const handleCategoryChange = (categoryId: string) => {
    form.setValue('category_id', categoryId, { shouldDirty: true, shouldValidate: true });
    form.setValue('type_id', '', { shouldDirty: true });
    form.setValue('subtype_id', '', { shouldDirty: true });
    const category = categories?.find((cat) => cat.id === categoryId);
    if (category) {
      form.setValue('normal_balance', category.normal_balance, { shouldDirty: true });
    }
  };

  // The normal balance follows the subtype on CREATE: a contra subtype (owner
  // draws, accumulated depreciation, …) carries its own side; any other gives
  // a top-level account its category's side and a sub-account its parent's.
  // A preview of trg_assign_account_number, which enforces it whatever is
  // sent. On edit the field is locked and update_account_classification moves
  // it with the subtype.
  const applySubtypeNormalBalance = (subtypeId: string | undefined) => {
    if (isEditing) return;
    const subtypeSide = orgSubtypes?.find((st) => st.id === subtypeId)?.normal_balance;
    const side =
      form.getValues('creation_type') === 'sub'
        ? childRuleSide(
            subtypeSide,
            filteredAccounts.find((acc) => acc.id === form.getValues('parent_account_id'))?.normal_balance,
          )
        : subtypeRuleSide(
            subtypeSide,
            categories?.find((cat) => cat.id === form.getValues('category_id'))?.normal_balance,
          );
    if (side) form.setValue('normal_balance', side, { shouldDirty: true });
  };

  const handleTypeChange = (typeId: string) => {
    form.setValue('type_id', typeId, { shouldDirty: true, shouldValidate: true });
    // Pre-fill the type's general subtype (the server's default too).
    const generalId = generalSubtypeIdOf(typeId);
    form.setValue('subtype_id', generalId ?? '', { shouldDirty: true });
    applySubtypeNormalBalance(generalId);
  };

  const handleSubtypeChange = (subtypeId: string) => {
    form.setValue('subtype_id', subtypeId, { shouldDirty: true });
    applySubtypeNormalBalance(subtypeId);
  };

  const handleParentChange = (value: string) => {
    form.setValue('parent_account_id', value, { shouldDirty: true, shouldValidate: true });
    const parent = filteredAccounts.find((acc) => acc.id === value);
    if (parent) {
      // A sub-account takes its type from the parent and, by default, its
      // subtype (the RPC does the same on re-parent; the user may then pick a
      // contra subtype of that type). Category/normal balance follow too on CREATE only —
      // on edit they are locked and the parent picker is already filtered to
      // the account's category.
      if (!isEditing) {
        form.setValue('category_id', parent.category_id, { shouldDirty: true, shouldValidate: true });
        form.setValue('normal_balance', parent.normal_balance, { shouldDirty: true });
      }
      form.setValue('type_id', parent.type_id, { shouldDirty: true, shouldValidate: true });
      // Keep a chosen subtype that is still allowed under the new parent (a
      // contra account moved to another capital account stays a draw);
      // otherwise take the parent's.
      const current = orgSubtypes?.find((st) => st.id === form.getValues('subtype_id'));
      const subtypeId = current && isChildSubtypeAllowed(current, parent) ? current.id : parent.subtype_id || '';
      form.setValue('subtype_id', subtypeId, { shouldDirty: true });
      applySubtypeNormalBalance(subtypeId);
    }
  };

  // Edit-mode parent candidates: same category, may hold children, not the
  // account itself or anything under it, and deep enough room for the moved
  // subtree (parent level + 1 + subtree depth <= cap).
  const editParentCandidates = useMemo(
    () =>
      isEditing && account
        ? filteredAccounts.filter(
            (acc) =>
              acc.id !== account.id &&
              !subtree.ids.has(acc.id) &&
              acc.category_id === account.category_id &&
              acc.can_have_children &&
              acc.account_level + 1 + subtree.depth <= MAX_ACCOUNT_DEPTH,
          )
        : [],
    [isEditing, account, filteredAccounts, subtree],
  );

  const handleClose = () => {
    form.reset(EMPTY_ACCOUNT_FORM);
    setCarbon(EMPTY_CARBON);
    setCarbonDirty(false);
  };

  // Persist the carbon config for an account (shared by create and edit). `hadExistingConfig`
  // selects update vs insert; when carbon is turned off we only need to flip an existing row.
  const saveCarbonConfig = async (accountId: string, hadExistingConfig: boolean) => {
    try {
      if (carbon.enabled && carbon.tableId) {
        const payload = {
          account_id: accountId,
          table_id: carbon.tableId,
          multiplier_dimension_code: carbon.multiplierCode || null,
          multiplier_dimension_label: carbon.multiplierLabel || null,
          multiplier_unit: carbon.multiplierUnit || null,
          multiplier_unit_code: carbon.multiplierUnitCode,
          dimension_filter: carbon.dimensionFilter,
          is_enabled: true,
        };
        if (hadExistingConfig) {
          await supabase
            .from('carbon_account_emission_tables')
            .update(payload)
            .eq('account_id', accountId);
        } else {
          await supabase.from('carbon_account_emission_tables').insert(payload);
        }
      } else if (!carbon.enabled && hadExistingConfig) {
        // Carbon turned off for an account that had a config — disable the existing row.
        // (Enabled-but-no-table is intentionally a no-op, matching the prior behavior.)
        await supabase
          .from('carbon_account_emission_tables')
          .update({ is_enabled: false })
          .eq('account_id', accountId);
      }
    } catch (error) {
      console.error('Error saving carbon configuration:', error);
      captureException(error as Error, { context: 'AccountModal.saveCarbonConfig', accountId });
      toast.warning(t('chartOfAccountsForm.messages.carbonConfigFailed'));
    }
  };

  const onSubmit = async (data: AccountFormData) => {
    if (isEditing && account) {
      // Send the opening fields ONLY when their read has resolved AND the user
      // actually changed them (dirtiness is measured against the seeded
      // journal value, see the resetField effect). An untouched save must not
      // carry them: before the read resolves the form still holds 0, and a
      // rename submitted in that window would otherwise reverse the account's
      // opening journal.
      const openingDirty =
        form.getFieldState('opening_balance').isDirty ||
        form.getFieldState('opening_balance_date').isDirty;
      const includeOpening = openingQuery.isSuccess && openingDirty && !openingLocked;
      // Classification rides along ONLY when the user touched it: a pristine
      // save must not call the RPC (it would no-op, but why round-trip). Full
      // target state — the RPC diffs against the row.
      const classificationDirty =
        !classificationLocked &&
        CLASSIFICATION_FIELDS.some((field) => form.getFieldState(field).isDirty);
      const isSub = data.creation_type === 'sub' && !!data.parent_account_id;
      const payload: UpdateAccountData = {
        account_name: data.account_name,
        account_description: data.account_description,
        can_have_children: data.can_have_children,
        requires_project: data.requires_project,
        default_tax_rate_id: data.default_tax_rate_id || null,
        ...(includeOpening
          ? {
              opening_balance: data.opening_balance ?? 0,
              opening_balance_date: data.opening_balance_date || null,
            }
          : {}),
        ...(classificationDirty
          ? {
              type_id: data.type_id,
              subtype_id: data.subtype_id || null,
              parent_account_id: isSub ? data.parent_account_id : null,
            }
          : {}),
        currency: data.currency || null,
      };
      const submitEdit = (body: UpdateAccountData) =>
        updateAccountMutation.mutate(
          { id: account.id, organizationId, data: body },
          {
            onError: async (error) => {
              const envelope = (
                error as { envelope?: { code?: string; error?: string; [k: string]: unknown } }
              ).envelope;
              // A manual normal-balance override would be replaced: ask, then
              // resend the same save with the answer (nothing was written).
              if (envelope?.code === NORMAL_BALANCE_CHOICE_REQUIRED) {
                const choice = await normalBalanceChoice.ask(
                  (envelope.accounts ?? []) as NormalBalanceChoiceConflict[],
                );
                if (choice) submitEdit({ ...body, normal_balance_choice: choice });
                return;
              }
              // Coded refusals of update_account_classification land on the
              // field they concern (the hook skips its toast for these).
              const code = envelope?.code as AccountClassificationErrorCode | undefined;
              const field = code ? ACCOUNT_CLASSIFICATION_ERROR_FIELD[code] : undefined;
              if (!field) return;
              // Envelope extras (max_depth, …) are the interpolation values; a
              // CONTRA_SUB_ACCOUNTS_BLOCK names the sub-accounts to move first.
              const accounts =
                code === CONTRA_SUB_ACCOUNTS_BLOCK
                  ? contraSubAccountBlocks(envelope?.accounts)
                      .map((a) => formatAccountLabel(a, showAccountNumbers))
                      .join(', ')
                  : undefined;
              form.setError(field, {
                type: 'server',
                message: t(`chartOfAccountsForm.errors.${code}`, {
                  ...envelope,
                  ...(accounts !== undefined ? { accounts } : {}),
                  defaultValue: envelope?.error ?? error.message,
                }),
              });
            },
            onSuccess: async () => {
              await saveCarbonConfig(account.id, !!existingCarbonConfig);
              // The account mutation already invalidated ['chartOfAccounts', org]
              // — but BEFORE the carbon write above landed, so the index grid's
              // carbon_tracking_enabled flag would refetch stale. Invalidate
              // again, after the await, so the grid sees the carbon change.
              queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
              queryClient.invalidateQueries({ queryKey: ['carbonConfig', account.id] });
              // Invalidate the hook consumers (TransactionCarbonInputs etc.) too,
              // which use a different query key than the modal's internal fetch.
              queryClient.invalidateQueries({ queryKey: ['account-carbon-config', account.id] });
              onOpenChange(false);
            },
          },
        );
      submitEdit(payload);
    } else {
      // Create new account
      const createData: CreateAccountData = {
        account_name: data.account_name,
        account_description: data.account_description,
        category_id: data.category_id,
        type_id: data.type_id,
        subtype_id: data.subtype_id || undefined,
        parent_account_id:
          data.creation_type === 'sub' && data.parent_account_id ? data.parent_account_id : undefined,
        normal_balance: data.normal_balance,
        can_have_children: data.can_have_children,
        requires_project: data.requires_project,
        opening_balance: data.opening_balance ?? 0,
        opening_balance_date: data.opening_balance_date || null,
        default_tax_rate_id: data.default_tax_rate_id || null,
        currency: data.currency || null,
      };

      createAccountMutation.mutate(
        { organizationId, data: createData },
        {
          onSuccess: async (newAccount) => {
            await saveCarbonConfig(newAccount.id, false);
            // Same as the edit path: re-invalidate the index AFTER the carbon
            // write so carbon_tracking_enabled is fresh on the refetch.
            queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
            queryClient.invalidateQueries({ queryKey: ['carbonConfig', newAccount.id] });
            queryClient.invalidateQueries({ queryKey: ['account-carbon-config', newAccount.id] });
            // The hook already toasted the opening-balance failure (partial
            // create) — a success toast on top of it would bury the warning.
            if (!newAccount.opening_balance_error) {
              toast.success(t('chartOfAccountsForm.messages.createSuccess'));
            }
            // Continue to the new account's detail page unless the caller opted out. Navigate
            // with `replace` BEFORE closing so it supersedes the dialog's history entry and the
            // wrapper skips its own history.back() (see FULLSCREEN_FORM_MODALS.md).
            if (navigateOnCreate) {
              orgNavigate(`/accounting/accounts/${newAccount.id}`, { replace: true });
            }
            onOpenChange(false);
          },
        },
      );
    }
  };

  // Type / Subtype selects, shared by create and edit. On edit the OBE type is
  // never offered (the RPC refuses it).
  const typeOptions = isEditing
    ? availableTypes.filter((accountType) => accountType.type_name !== OBE_TYPE_NAME)
    : availableTypes;
  const typeField = (
    <FormField
      control={form.control}
      name="type_id"
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel className={FIELD_LABEL}>{t('chartOfAccountsForm.fields.type')}</FormLabel>
            {fieldState.error && (
              <span className="text-xs text-destructive">{fieldState.error.message}</span>
            )}
          </div>
          <Select
            value={field.value || ''}
            onValueChange={handleTypeChange}
            disabled={!selectedCategoryId || typeInherited}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={t('chartOfAccountsForm.placeholders.selectType')} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {typeOptions.map((accountType) => (
                <SelectItem key={accountType.id} value={accountType.id}>
                  {accountType.type_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isEditing && typeInherited && (
            <FormDescription>{t('chartOfAccountsForm.help.parentInheritanceEdit')}</FormDescription>
          )}
        </FormItem>
      )}
    />
  );
  // Subtype picker: name, with the house code as the muted second line and
  // both codes searchable. On edit, the account's current subtype stays
  // listed even if the org's jurisdiction no longer offers it. Under a parent
  // only the parent's subtype and the contra subtypes of its type qualify
  // (coa_child_subtype_ok — the server refuses anything else).
  const subtypeOptions = useMemo<SearchableSelectOption[]>(() => {
    const selectedParent = typeInherited
      ? filteredAccounts.find((acc) => acc.id === selectedParentId)
      : undefined;
    const allowed = (st: { id: string; type_id: string; normal_balance?: 'debit' | 'credit' | null }) =>
      !selectedParent || isChildSubtypeAllowed(st, selectedParent);
    const list = (orgSubtypes ?? []).filter((st) => st.type_id === selectedTypeId && allowed(st));
    const current = account?.subtype;
    if (
      isEditing &&
      current &&
      current.type_id === selectedTypeId &&
      allowed(current) &&
      !list.some((st) => st.id === current.id)
    ) {
      list.push(current);
    }
    return list.map((st) => ({
      value: st.id,
      label: st.subtype_name,
      description: st.house_code,
      keywords: [st.subtype_code, st.house_code ?? ''].filter(Boolean),
    }));
  }, [
    orgSubtypes,
    selectedTypeId,
    isEditing,
    account?.subtype,
    typeInherited,
    selectedParentId,
    filteredAccounts,
  ]);
  const subtypeField = (
    <FormField
      control={form.control}
      name="subtype_id"
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel className={FIELD_LABEL}>{t('chartOfAccountsForm.fields.subtype')}</FormLabel>
            {fieldState.error && (
              <span className="text-xs text-destructive">{fieldState.error.message}</span>
            )}
          </div>
          <FormControl>
            <SearchableSelect
              options={subtypeOptions}
              value={field.value || ''}
              onValueChange={handleSubtypeChange}
              placeholder={t('chartOfAccountsForm.placeholders.selectSubtype')}
              searchPlaceholder={t('chartOfAccountsForm.placeholders.searchSubtypes')}
              emptyText={t('chartOfAccountsForm.placeholders.noSubtypesFound')}
              disabled={!selectedTypeId}
            />
          </FormControl>
          {!typeInherited && (
            <FormDescription>{t('chartOfAccountsForm.help.subtype')}</FormDescription>
          )}
        </FormItem>
      )}
    />
  );

  // Read-only: the number is assigned from the subtype's house code (a
  // sub-account extends its parent's number) and follows a reclassification.
  const accountNumberField = (
    <div>
      <p className={`${FIELD_LABEL} mb-1`}>{t('chartOfAccountsForm.fields.accountNumber')}</p>
      <Input
        value={account?.account_number ?? ''}
        placeholder={t('chartOfAccountsForm.placeholders.assignedAutomatically')}
        aria-label={t('chartOfAccountsForm.fields.accountNumber')}
        className="font-mono"
        readOnly
        disabled
      />
      <p className="text-xs text-muted-foreground mt-1">
        {t('chartOfAccountsForm.help.accountNumberAssigned')}
      </p>
    </div>
  );

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('chartOfAccountsForm.editTitle') : t('chartOfAccountsForm.createTitle')}
      onSubmit={form.handleSubmit(onSubmit)}
      submitLabel={isEditing ? t('chartOfAccountsForm.updateSubmit') : t('chartOfAccountsForm.createSubmit')}
      isSubmitting={isEditing ? updateAccountMutation.isPending : createAccountMutation.isPending}
      formId="account-form"
      skipCloseConfirmation={!form.formState.isDirty && !carbonDirty}
      onClose={handleClose}
    >
      <Form {...form}>
        <div className="space-y-8">
          <Card>
            <CardContent className="pt-6 space-y-8">
              {/* Hero — the artifact's name, which IS its type once one is known:
                  "Bank", "Credit Cards", "Accounts Payable". Deliberately no
                  "Account" suffix (Clay, 2026-09-14): several `type_name` values
                  are already complete noun phrases, so appending it produced
                  "Retained Earnings Account" and "Opening Balance Equity Account".
                  The type is database data and is NOT translated, matching the
                  read-only Classification row below. Falls back to the generic
                  noun while creating, before a type has been chosen. */}
              <h2 className="text-[40px] font-extralight leading-tight text-right uppercase">
                {selectedTypeName ?? resolvedTypeName ?? t('chartOfAccountsForm.heroTitle')}
              </h2>

              {/* SECTION: Basic Information */}
              <div className="space-y-4">
                <h3 className={SECTION_HEAD}>{t('chartOfAccountsForm.sections.basicInfo')}</h3>
                <FormField
                  control={form.control}
                  name="account_name"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className={FIELD_LABEL}>
                          {t('chartOfAccountsForm.fields.accountName')}
                        </FormLabel>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </div>
                      <FormControl>
                        <Input
                          placeholder={t('chartOfAccountsForm.placeholders.accountName')}
                          className={fieldState.error ? 'border-destructive' : ''}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {accountNumberField}
                <FormField
                  control={form.control}
                  name="account_description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={FIELD_LABEL}>
                        {t('chartOfAccountsForm.fields.description')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('chartOfAccountsForm.placeholders.description')}
                          rows={2}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* SECTION: Account Classification */}
              <div className="space-y-4">
                <h3 className={SECTION_HEAD}>{t('chartOfAccountsForm.sections.classification')}</h3>

                {!classificationLocked ? (
                  <>
                    <FormField
                      control={form.control}
                      name="creation_type"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormDescription>
                            {t(
                              isEditing
                                ? 'chartOfAccountsForm.help.nestingEdit'
                                : 'chartOfAccountsForm.help.creationType',
                            )}
                          </FormDescription>
                          <FormControl>
                            <RadioGroup
                              value={field.value}
                              onValueChange={(value) => handleCreationTypeChange(value as 'main' | 'sub')}
                              className="flex gap-6"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="main" id="creation-type-main" />
                                <FormLabel htmlFor="creation-type-main" className="font-normal">
                                  {t(
                                    isEditing
                                      ? 'chartOfAccountsForm.options.topLevelAccount'
                                      : 'chartOfAccountsForm.options.createMainAccount',
                                  )}
                                </FormLabel>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="sub" id="creation-type-sub" />
                                <FormLabel htmlFor="creation-type-sub" className="font-normal">
                                  {t(
                                    isEditing
                                      ? 'chartOfAccountsForm.options.subAccountOf'
                                      : 'chartOfAccountsForm.options.createSubAccount',
                                  )}
                                </FormLabel>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {isEditing ? (
                      <>
                        {creationType === 'sub' && (
                          <FormField
                            control={form.control}
                            name="parent_account_id"
                            render={({ field, fieldState }) => (
                              <FormItem>
                                <div className="flex items-center gap-2">
                                  <FormLabel className={FIELD_LABEL}>
                                    {t('chartOfAccountsForm.fields.parentAccount')}
                                  </FormLabel>
                                  {fieldState.error && (
                                    <span className="text-xs text-destructive">
                                      {fieldState.error.message}
                                    </span>
                                  )}
                                </div>
                                <FormControl>
                                  <AccountSelect
                                    accounts={editParentCandidates}
                                    value={field.value || ''}
                                    onChange={handleParentChange}
                                    placeholder={t('chartOfAccountsForm.placeholders.selectParentAccount')}
                                  />
                                </FormControl>
                                <FormDescription>
                                  {t('chartOfAccountsForm.help.parentInheritanceEdit')}
                                </FormDescription>
                              </FormItem>
                            )}
                          />
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className={`${FIELD_LABEL} mb-1`}>{t('chartOfAccountsForm.fields.category')}</p>
                            <p className="text-sm font-medium">
                              {account?.category?.category_name || t('chartOfAccountsForm.notAvailable')}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {t('chartOfAccountsForm.help.categoryLocked')}
                            </p>
                          </div>
                          {typeField}
                          {subtypeField}
                        </div>
                        {childCount > 0 && (
                          <FormDescription>
                            {t('chartOfAccountsForm.help.cascadeToChildren', { count: childCount })}
                          </FormDescription>
                        )}
                        {isFeedAccountType(resolvedTypeName) && (
                          <FormDescription>{t('chartOfAccountsForm.help.feedTypeLocked')}</FormDescription>
                        )}
                      </>
                    ) : creationType === 'main' ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="category_id"
                            render={({ field, fieldState }) => (
                              <FormItem>
                                <div className="flex items-center gap-2">
                                  <FormLabel className={FIELD_LABEL}>
                                    {t('chartOfAccountsForm.fields.category')}
                                  </FormLabel>
                                  {fieldState.error && (
                                    <span className="text-xs text-destructive">
                                      {fieldState.error.message}
                                    </span>
                                  )}
                                </div>
                                <Select value={field.value || ''} onValueChange={handleCategoryChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={t('chartOfAccountsForm.placeholders.selectCategory')}
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {categories?.map((category) => (
                                      <SelectItem key={category.id} value={category.id}>
                                        {category.category_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />

                          {typeField}
                          {subtypeField}
                        </div>

                        {/* Advanced — Normal Balance override */}
                        <Accordion type="single" collapsible>
                          <AccordionItem value="advanced" className="border-none">
                            <AccordionTrigger className="py-2 text-sm text-muted-foreground hover:no-underline">
                              {t('chartOfAccountsForm.options.advanced')}
                            </AccordionTrigger>
                            <AccordionContent>
                              <FormField
                                control={form.control}
                                name="normal_balance"
                                render={({ field }) => (
                                  <FormItem className="pt-2">
                                    <FormLabel className={FIELD_LABEL}>
                                      {t('chartOfAccountsForm.fields.normalBalance')}
                                    </FormLabel>
                                    <Select
                                      value={field.value}
                                      onValueChange={(value: 'debit' | 'credit') =>
                                        form.setValue('normal_balance', value, { shouldDirty: true })
                                      }
                                      disabled={!selectedCategoryId}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="w-1/3 mt-1">
                                          <SelectValue
                                            placeholder={
                                              selectedCategoryId
                                                ? t('chartOfAccountsForm.placeholders.autoSetByCategory')
                                                : t('chartOfAccountsForm.placeholders.selectCategoryFirst')
                                            }
                                          />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="debit">
                                          {t('chartOfAccountsForm.options.debit')}
                                        </SelectItem>
                                        <SelectItem value="credit">
                                          {t('chartOfAccountsForm.options.credit')}
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormDescription>
                                      {t('chartOfAccountsForm.help.normalBalance')}
                                    </FormDescription>
                                  </FormItem>
                                )}
                              />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </>
                    ) : (
                      <FormField
                        control={form.control}
                        name="parent_account_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className={FIELD_LABEL}>
                                {t('chartOfAccountsForm.fields.parentAccount')}
                              </FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">
                                  {fieldState.error.message}
                                </span>
                              )}
                            </div>
                            <FormControl>
                              <AccountSelect
                                accounts={filteredAccounts.filter(
                                  (acc) => acc.can_have_children && acc.account_level < 3,
                                )}
                                value={field.value || ''}
                                onChange={handleParentChange}
                                placeholder={t('chartOfAccountsForm.placeholders.selectParentAccount')}
                              />
                            </FormControl>
                            <FormDescription>
                              {t('chartOfAccountsForm.help.parentInheritance')}
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    )}
                    {!isEditing && creationType === 'sub' && typeInherited && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {typeField}
                        {subtypeField}
                      </div>
                    )}
                  </>
                ) : (
                  /* Read-only classification: system + OBE accounts (the RPC
                     refuses them — SYSTEM_ACCOUNT / OBE_ACCOUNT). */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground">
                        {t('chartOfAccountsForm.help.systemAccountLocked')}
                      </p>
                    </div>
                    <div>
                      <p className={`${FIELD_LABEL} mb-1`}>{t('chartOfAccountsForm.fields.category')}</p>
                      <p className="text-sm font-medium">
                        {account?.category?.category_name || t('chartOfAccountsForm.notAvailable')}
                      </p>
                    </div>
                    <div>
                      <p className={`${FIELD_LABEL} mb-1`}>{t('chartOfAccountsForm.fields.type')}</p>
                      <p className="text-sm font-medium">
                        {account?.type?.type_name || t('chartOfAccountsForm.notAvailable')}
                      </p>
                    </div>
                    {account?.parent_account_id && (
                      <div>
                        <p className={`${FIELD_LABEL} mb-1`}>
                          {t('chartOfAccountsForm.fields.parentAccount')}
                        </p>
                        <p className="text-sm font-medium">
                          {filteredAccounts.find((a) => a.id === account.parent_account_id)
                            ?.account_name || t('chartOfAccountsForm.notAvailable')}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className={`${FIELD_LABEL} mb-1`}>
                        {t('chartOfAccountsForm.fields.normalBalance')}
                      </p>
                      <p className="text-sm font-medium capitalize">
                        {account?.normal_balance || t('chartOfAccountsForm.notAvailable')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* TINTED BAND: Financial Settings */}
              <div className="-mx-6 px-6 py-6 bg-muted space-y-5">
                <h3 className={SECTION_HEAD}>{t('chartOfAccountsForm.sections.financialSettings')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="opening_balance"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={FIELD_LABEL}>
                          {t('chartOfAccountsForm.fields.openingBalance')}
                        </FormLabel>
                        <FormControl>
                          <NumberInput
                            value={field.value ?? ''}
                            onChange={(value) => field.onChange(value === '' ? 0 : Number(value))}
                            placeholder="0.00"
                            disabled={openingLoading || openingLocked}
                          />
                        </FormControl>
                        {fieldState.error && (
                          <p className="text-sm text-destructive">{fieldState.error.message}</p>
                        )}
                        {openingLocked && (
                          <FormDescription>
                            {isObeAccount
                              ? t('chartOfAccountsForm.help.openingBalanceObeAccount')
                              : t('chartOfAccountsForm.help.openingBalanceShared')}
                          </FormDescription>
                        )}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="opening_balance_date"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={FIELD_LABEL}>
                          {t('chartOfAccountsForm.fields.openingBalanceDate')}
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            value={parseLocalISODate(field.value)}
                            onChange={(date) => field.onChange(formatLocalISODate(date) || '')}
                            placeholder={t('chartOfAccountsForm.placeholders.selectDate')}
                            disabled={openingLoading || openingLocked}
                          />
                        </FormControl>
                        {fieldState.error && (
                          <p className="text-sm text-destructive">{fieldState.error.message}</p>
                        )}
                        <FormDescription>
                          {t('chartOfAccountsForm.help.openingBalanceDate')}
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={FIELD_LABEL}>
                          {t('chartOfAccountsForm.fields.currency')}
                        </FormLabel>
                        <FormControl>
                          <CurrencySelector
                            value={field.value ?? ''}
                            onValueChange={field.onChange}
                            defaultOptionLabel={t('chartOfAccountsForm.options.organizationDefault', {
                              currency: selectedOrganization?.currency || 'EUR',
                            })}
                          />
                        </FormControl>
                        <FormDescription>
                          {t('chartOfAccountsForm.help.currencyOverride')}
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="default_tax_rate_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={FIELD_LABEL}>
                          {t('chartOfAccountsForm.fields.defaultTaxRate')}
                        </FormLabel>
                        <Select
                          value={field.value || '_none'}
                          onValueChange={(value) => field.onChange(value === '_none' ? '' : value)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t('chartOfAccountsForm.placeholders.noDefaultTaxRate')}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="_none">
                              {t('chartOfAccountsForm.placeholders.noDefaultTaxRate')}
                            </SelectItem>
                            {recommendedRates.length > 0 && (
                              <SelectGroup>
                                <SelectLabel>
                                  {selectedCategoryName
                                    ? t('chartOfAccountsForm.options.recommendedFor', {
                                        category: selectedCategoryName,
                                      })
                                    : t(`tax.ui.settings.direction.${recommendedTaxDirection.toLowerCase()}`)}
                                </SelectLabel>
                                {recommendedRates.map((rate) => (
                                  <SelectItem key={rate.id} value={rate.id}>
                                    {rate.name} ({rate.rate}%)
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            {otherRates.length > 0 && (
                              <>
                                {recommendedRates.length > 0 && <SelectSeparator />}
                                <SelectGroup>
                                  <SelectLabel>
                                    {t(
                                      `tax.ui.settings.direction.${
                                        recommendedTaxDirection === 'OUTPUT' ? 'input' : 'output'
                                      }`,
                                    )}
                                  </SelectLabel>
                                  {otherRates.map((rate) => (
                                    <SelectItem key={rate.id} value={rate.id}>
                                      {rate.name} ({rate.rate}%)
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          {t('chartOfAccountsForm.help.defaultTaxRate')}
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* SECTION: Account Options */}
              <div className="space-y-4">
                <h3 className={SECTION_HEAD}>{t('chartOfAccountsForm.sections.accountOptions')}</h3>
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="can_have_children"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="mt-0! text-sm font-normal">
                          {t('chartOfAccountsForm.options.canHaveChildren')}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="requires_project"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="mt-0! text-sm font-normal">
                          {t('chartOfAccountsForm.options.requiresProject')}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* SECTION: Carbon Tracking */}
              <div className="space-y-4">
                <h3 className={SECTION_HEAD}>{t('chartOfAccountsForm.sections.carbonTracking')}</h3>
                <AccountCarbonSettings
                  enabled={carbon.enabled}
                  onEnabledChange={carbonHandlers.onEnabledChange}
                  tableId={carbon.tableId}
                  onTableIdChange={carbonHandlers.onTableIdChange}
                  multiplierCode={carbon.multiplierCode}
                  onMultiplierCodeChange={carbonHandlers.onMultiplierCodeChange}
                  multiplierLabel={carbon.multiplierLabel}
                  onMultiplierLabelChange={carbonHandlers.onMultiplierLabelChange}
                  multiplierUnit={carbon.multiplierUnit}
                  onMultiplierUnitChange={carbonHandlers.onMultiplierUnitChange}
                  dimensionFilter={carbon.dimensionFilter}
                  onDimensionFilterChange={carbonHandlers.onDimensionFilterChange}
                  multiplierUnitCode={carbon.multiplierUnitCode}
                  onMultiplierUnitCodeChange={carbonHandlers.onMultiplierUnitCodeChange}
                />
              </div>

              {/* SECTION: Bank feed — LAST in the card because it holds Disconnect,
                  the one destructive control on this form. Unlike every section
                  above it, nothing here is saved by Submit: it writes immediately.

                  The gate lives here rather than inside the section so the
                  section's own `useBankConnections` never fires for a create, a
                  non-feed account, a flag-off org, or a host that cannot take the
                  connect redirect. */}
              {/* TINTED BAND: Bank Feed — the SECOND band in this form, a
                  deliberate deviation from the one-band recipe (Clay,
                  2026-09-14; noted in docs/FULLSCREEN_FORM_MODALS.md). Theme-
                  aware `bg-muted`, not the frozen paper band: the section
                  embeds shared components whose text comes from theme tokens,
                  which would go light-on-light in dark mode on a frozen band. */}
              {isEditing && account && syncEnabled && isFeedAccountType(resolvedTypeName) && onRequestBankConnect && (
                /* `-mb-6` bleeds the band through CardContent's bottom padding to
                   the card edge — sound ONLY because this is the LAST section.
                   `rounded-b-lg` matches the Card's own radius so the corners
                   meet; rounding here rather than `overflow-hidden` on the Card
                   keeps portaled popovers (AccountSelect, Select) unclipped. */
                <div className="-mx-6 -mb-6 px-6 py-6 bg-muted space-y-5 rounded-b-lg">
                  <h3 className={SECTION_HEAD}>
                    {t('chartOfAccountsForm.sections.bankFeed', {
                      context: isCreditCardType(resolvedTypeName) ? 'card' : undefined,
                    })}
                  </h3>
                  <AccountBankFeedSection
                    organizationId={account.organization_id}
                    accountId={account.id}
                    accountName={account.account_name}
                    accountTypeName={resolvedTypeName ?? ''}
                    accountCurrency={account.currency ?? null}
                    // Carbon edits are unsaved work too — `isDirty` alone would
                    // let a redirect discard them without asking.
                    isHostFormDirty={form.formState.isDirty || carbonDirty}
                    onRequestBankConnect={onRequestBankConnect}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Form>
      {normalBalanceChoice.dialog}
    </FullscreenFormDialog>
  );
}
